import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { UserContext } from '../../../context/authContext';
import { useAlert } from '../../../context/alertContext';
import axios from 'axios';
import { AsYouType } from 'libphonenumber-js';
import { decode } from 'jsonwebtoken';
import { Loader2 } from 'lucide-react';
import { Address } from '../../../lib/models/interfaces';
import { useSearchParams } from 'react-router-dom';
import {
  primaryButtonGreen,
  primaryGreenDarker
} from '../../tokenStyle/tokeReset';

enum AddressNames {
  street = 'street',
  city = 'city',
  state = 'state',
  zip = 'zip',
  country = 'country'
}

const getUserInfo = (token: string) => {
  const decodedToken = decode(token) as unknown as any;
  console.log('decoded token', decodedToken);
  return decodedToken;
};
/**
 * CreateAccountNew
 * ---------------
 * - Company Name, Primary Contact, Telephone Number, Full Address
 * - Styled to match LoginPageNew.tsx
 * - No state/logic, just layout and styling
 */
const UpdateAccount: React.FC = () => {
  const { auth, token, companyAccount, users } = useContext(UserContext);
  const { setAlert } = useAlert();
  const [searchParams] = useSearchParams();

  /// state
  const [address, setAddress] = useState<Address>({
    street: '',
    city: '',
    state: '',
    zip: '',
    country: ''
  });
  const [companyName, setCompanyName] = useState<string>('');
  const [primaryContact, setPrimaryContact] = useState<string>('');
  const [tel, setTel] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [primaryEmail, setPrimaryEmail] = useState<string>('');
  const [accountManager, setAccountManager] = useState<string>('');

  const [emailError, setEmailError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [createAccountLoading, setCreateAccountLoading] =
    useState<boolean>(false);

  const [currentAccountInfo, setCurrentAccountInfo] = useState();

  //   use effects

  useEffect(() => {
    if (!token) return;

    loadAccountInfo();
  }, []);

  const loadAccountInfo = () => {
    if (!companyAccount) return;
    setCompanyName(companyAccount?.companyName);
    setPrimaryContact(companyAccount?.primaryAdmin);
    setPrimaryEmail(companyAccount?.primaryEmail);
    setTel(companyAccount?.contactNumber);
    setAddress(companyAccount?.address);
  };

  // functions

  const handleChange = (detail: string, name: AddressNames) => {
    console.log('handle change', detail, name);
    const temp = address;
    temp[name] = detail;
    console.log('combo', temp[name]);
    // const str = JSON.stringify(temp);
    // const parse = JSON.parse(str);
    setAddress({ ...temp });
  };

  const updateAccount = async () => {
    if (!token) return;
    try {
      console.log('token account', token);
      setCreateAccountLoading(true);
      const user = getUserInfo(token);
      console.log('user', user);

      console.log('sub', user.sub, 'username', user.username);
      const account = {
        companyName: companyName,
        address: address,
        contactNumber: tel,
        primaryAdmin: primaryContact,
        primaryEmail: primaryEmail
      };
      if (
        !account.address ||
        !account.companyName ||
        !account.contactNumber ||
        !account.primaryAdmin ||
        !account.primaryEmail
      ) {
        alert('Missing Info');
        return;
      }
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/account?type=updateAccount`,
        {
          account,
          customerId: companyAccount?.customerId
        },
        auth
      );
      setAlert(`Account Updated`, 'success', 3);
      console.log('new customer created', JSON.stringify(result.data));
      const accountNew = result.data.result.account;
      setCreateAccountLoading(false);
    } catch (e) {
      const error = e as Error;
      setCreateAccountLoading(false);
      setAlert(`There was an error - ${error.message}`, 'error');
    }
  };

  const handleChangeContact = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const opt = e.target.selectedOptions[0] as HTMLOptionElement;
    const value = opt.value;
    const email = opt.dataset.email;
    if (!value || !email) return;
    setPrimaryContact(value);
    setPrimaryEmail(email);
  };

  const handleReset = () => {
    setAddress({
      street: '',
      city: '',
      state: '',
      zip: '',
      country: ''
    });
    setAccountManager('');
    setCompanyName('');
    setTel('');
    setPrimaryContact('');
    setPrimaryEmail('');
    setNotes('');
    setEmailError('');
  };
  const disableButton = useMemo(() => {
    const regex = /^\(\d{3}\) \d{3}-\d{4}$/;
    const isTelValid = regex.test(tel);
    if (
      companyName === '' ||
      !isTelValid ||
      primaryContact === '' ||
      address.street === '' ||
      address.city === '' ||
      address.state === '' ||
      address.country === ''
    ) {
      return true;
    } else {
      return false;
    }
  }, [companyName, tel, primaryContact, address]);

  const updateOnly = searchParams.get('update-only') ?? null;

  const handleChangePlan = async () => {
    if (!token) return;
    try {
      setLoading(true);
      let returnUrl;

      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/stripe-portal`,
        {
          returnUrl: `${process.env.REACT_APP_BASE_URL}/app/landing`
        },
        auth
      );
      const url = result.data.portalUrl;
      setLoading(false);
      window.location.href = url;
    } catch (e) {
      const error = e as Error;
      setLoading(false);
      setAlert(`${error.message}`, 'error');
    }
  };
  const buttonStyle = `flex items-center justify-center w-[40%] rounded-xl bg-[${primaryButtonGreen}] px-4 py-2.5 text-white transition hover:bg-[${primaryGreenDarker}] disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900`;
  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative z-10 w-full max-w-3xl"
      >
        <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white/80 dark:bg-slate-900/70 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]">
          {/* Header */}
          <div className="px-8 pt-8 pb-4 text-center">
            <img
              src="/korulogo.png"
              alt="Koru Logo"
              className="mx-auto mb-4 h-12 w-12 object-contain"
              loading="eager"
              decoding="async"
            />
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
              Update Account
            </h1>
          </div>
          {/* Form */}
          <form
            className=" px-8 pb-8 pt-4 space-y-5"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* LEFT: Company / Contact / Tel */}
              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="companyName"
                    className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Company Name
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    placeholder="Your Company Inc."
                    onChange={(e) => setCompanyName(e.target.value)}
                    value={companyName}
                    className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                  />
                </div>
                <div>
                  <label
                    htmlFor="primaryContact"
                    className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Primary Contact
                  </label>
                  <select
                    id="primaryUser"
                    name="primaryContact"
                    className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                    onChange={handleChangeContact}
                    value={primaryContact}
                  >
                    {users
                      .filter((adminU) => adminU.isAdmin)
                      .map((user) => {
                        return (
                          <option
                            key={user.sub}
                            value={user.name}
                            data-email={user.email}
                          >
                            {user.name}
                          </option>
                        );
                      })}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="telephone"
                    className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Telephone Number
                  </label>
                  <input
                    id="telephone"
                    name="telephone"
                    type="text"
                    placeholder="(555) 867 5309"
                    value={tel}
                    onChange={(e) => {
                      if (e.target.value.length <= 5) {
                        setTel(e.target.value);
                      } else {
                        const newNumber = new AsYouType('US').input(
                          e.target.value
                        );
                        setTel(newNumber);
                      }
                    }}
                    className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                    autoComplete="tel"
                  />
                </div>
              </div>
              {/* RIGHT: Address */}
              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="street"
                    className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Street
                  </label>
                  <input
                    id="street"
                    name="street"
                    type="text"
                    placeholder="123 Main St"
                    value={address?.street}
                    onChange={(e) =>
                      handleChange(e.target.value, AddressNames.street)
                    }
                    className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                    autoComplete="street-address"
                  />
                </div>
                <div>
                  <label
                    htmlFor="city"
                    className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    City
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    placeholder="Seattle"
                    value={address?.city}
                    onChange={(e) =>
                      handleChange(e.target.value, AddressNames.city)
                    }
                    className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                  />
                </div>
                <div>
                  <label
                    htmlFor="state"
                    className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    State
                  </label>
                  <input
                    id="state"
                    name="state"
                    type="text"
                    placeholder="Washington"
                    value={address?.state}
                    onChange={(e) =>
                      handleChange(e.target.value, AddressNames.state)
                    }
                    className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                  />
                </div>
                <div>
                  <label
                    htmlFor="state"
                    className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Zip
                  </label>
                  <input
                    id="zip"
                    name="zip"
                    type="text"
                    placeholder="98100"
                    value={address?.zip}
                    onChange={(e) =>
                      handleChange(e.target.value, AddressNames.zip)
                    }
                    className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                  />
                </div>
                <div>
                  <label
                    htmlFor="country"
                    className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Country
                  </label>
                  {/* <input
                    id="country"
                    name="country"
                    type="text"
                    placeholder="USA"
                    value={address.country}
                    onChange={(e) =>
                      handleChange(e.target.value, AddressNames.country)
                    }
                    className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                  /> */}
                  <select
                    id="country"
                    name="country"
                    className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                    onChange={(e) =>
                      handleChange(e.target.value, AddressNames.country)
                    }
                    value={address?.country}
                  >
                    <option value="">Select Country</option>
                    <option value="USA">USA</option>
                  </select>
                </div>
              </div>
            </div>
            {emailError && (
              <p
                className="text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                {emailError}
              </p>
            )}
            <div className="w-full flex items-center justify-around">
              <button
                onClick={() => handleChangePlan()}
                className={buttonStyle}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-2 w-2 animate-spin" />
                    Creating Checkout Session
                  </>
                ) : (
                  <>Change Plan</>
                )}
              </button>
              <button
                className={buttonStyle}
                onClick={() => updateAccount()}
                disabled={disableButton}
              >
                {createAccountLoading ? (
                  <>
                    <Loader2 className="h-2 w-2 animate-spin" />
                    Updating Information
                  </>
                ) : (
                  <>Update Account Information</>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default UpdateAccount;
